import { Check, ChevronDown } from 'lucide-react'
import { useId, useState, type ReactNode } from 'react'

import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { portalScroll } from '@/lib/portalScroll'
import { cn } from '@/lib/utils'

export type PickerOption = {
  value: string
  label: string
  hint?: string
  /** Shown before the label, in the list and on the trigger: an icon or a flag. */
  icon?: ReactNode
}

/**
 * One labelled, searchable single choice (from the Costs page, PR #13). A list of hundreds (retailers)
 * stays a short scrolling list with a search box, never a menu the height
 * of the screen. `emptyLabel` is the "no choice" row and the trigger's text.
 */
export function Picker({
  label,
  options,
  value,
  onChange,
  emptyLabel,
  searchLabel,
  noMatches,
  pending = false,
  error,
  className,
  hideLabel = false,
  size = 'md',
}: {
  label: string
  options: readonly PickerOption[]
  value: string | undefined
  onChange: (next: string | undefined) => void
  emptyLabel: string
  searchLabel: string
  noMatches: string
  pending?: boolean
  /** Why the choice is refused, tied to the field for assistive tech. */
  error?: string
  className?: string
  /** Keep the label for assistive tech only — a grid cell, whose column header names it. */
  hideLabel?: boolean
  /** `sm`: a grid row's height rather than a form field's. */
  size?: 'md' | 'sm'
}) {
  const id = useId()
  const errorId = `${id}-error`
  const [open, setOpen] = useState(false)
  const selected = options.find((option) => option.value === value)

  return (
    <div className={cn('grid min-w-0 gap-1.5', className)}>
      <Label htmlFor={id} variant="secondary" className={cn(hideLabel && 'sr-only')}>
        {label}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? errorId : undefined}
            disabled={pending}
            className={cn(
              'border-input bg-background hover:border-ring focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-error flex w-full min-w-0 items-center gap-2 rounded-md border text-left text-sm outline-none focus-visible:ring-[3px] disabled:opacity-60',
              size === 'sm' ? 'h-7 px-2' : 'h-9 px-3',
            )}
          >
            {selected?.icon ? (
              <span className="flex shrink-0 items-center" aria-hidden="true">
                {selected.icon}
              </span>
            ) : null}
            <span className={cn('min-w-0 flex-1 truncate', !selected && 'text-muted-foreground')}>
              {selected?.label ?? emptyLabel}
            </span>
            <ChevronDown className="text-muted-foreground size-4 shrink-0" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-0">
          <Command label={searchLabel}>
            <CommandInput placeholder={searchLabel} />
            <CommandList className="max-h-72" {...portalScroll}>
              <CommandEmpty>{noMatches}</CommandEmpty>
              <CommandItem
                value={`__none ${emptyLabel}`}
                onSelect={() => {
                  onChange(undefined)
                  setOpen(false)
                }}
              >
                <Check
                  className={cn('size-4', value === undefined ? 'opacity-100' : 'opacity-0')}
                />
                <span className="text-muted-foreground">{emptyLabel}</span>
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${option.value}`}
                  onSelect={() => {
                    onChange(option.value)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn('size-4', option.value === value ? 'opacity-100' : 'opacity-0')}
                  />
                  {option.icon ? (
                    <span className="flex shrink-0 items-center" aria-hidden="true">
                      {option.icon}
                    </span>
                  ) : null}
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {option.hint ? (
                    <span className="text-muted-foreground text-xs">{option.hint}</span>
                  ) : null}
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error ? (
        <p id={errorId} role="alert" className="text-destructive text-xs">
          {error}
        </p>
      ) : null}
    </div>
  )
}
